import React, { useContext, useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import { N800, N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { MenuGroup } from '@atlaskit/menu';
import Heading from '@atlaskit/heading';

import { SPAViewContext } from '@confluence/spa-view-context';
import { EditionAwarenessDropdownExperimentLoadable } from '@confluence/experiment-confluence-ea-dropdown/entry-points/ui/EditionAwarenessDropdownExperiment';
import type { BillingSourceSystem } from '@confluence/experiment-confluence-ea-dropdown/entry-points/utils';

import { useBillingUrls } from '../useBillingUrls';

import type { ActionMenuButtonsType } from './ActionMenuButtons';
import { ActionMenuButtons } from './ActionMenuButtons';
import { PremiumImage } from './Images';
import { getPillType } from './getPillType';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupBackground = styled.div({
	backgroundRepeat: 'no-repeat',
	backgroundPosition: 'center',
	borderRadius: '3px',
	width: '100%',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PremiumPopupBackground = styled(PopupBackground)({
	backgroundImage: `linear-gradient(45deg,
    ${token('color.background.accent.blue.bolder', '#1D7AFC')} 0%,
    ${token('color.background.accent.purple.bolder', '#8270DB')} 98.15%)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageContainer = styled.div({
	overflow: 'hidden',
	width: '100%',
	height: '107px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupBodyContainer = styled.div({
	padding: `${token('space.100', '8px')} 0`,
	color: token('color.text', N800),
	borderBottomLeftRadius: '3px',
	borderBottomRightRadius: '3px',
	backgroundColor: token('elevation.surface.raised', N0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PremiumHeaderTitle = styled.div({
	padding: `${token('space.200')} ${token('space.200')} ${token(
		'space.075',
	)} ${token('space.200')}`,
});

const i18n = defineMessages({
	premiumTrial: {
		id: 'trial-components.premium.trial.title',
		defaultMessage: 'Premium trial',
		description: 'Premium trial title',
	},
});
interface ActionMenuItemProps extends ActionMenuButtonsType {
	canAddBillingForTrial?: boolean;
	billingSourceSystem?: string | null;
	isTreatmentForEaPremiumDropdownExperiment?: boolean;
	daysLeftInTrial?: number | null;
}

export const PremiumActionMenuItems = ({
	hasBillingInformation,
	isPreDunningPeriod,
	trialEndDate,
	canAddBillingForTrial,
	billingSourceSystem,
	isTreatmentForEaPremiumDropdownExperiment,
	daysLeftInTrial,
}: Omit<ActionMenuItemProps, 'edition'>) => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { paymentFlowUrl, changePlanUrl } = useBillingUrls(
		// Skip this call if we are not in the experiment
		!isTreatmentForEaPremiumDropdownExperiment,
	);

	if (isTreatmentForEaPremiumDropdownExperiment) {
		return (
			<div data-testid="premium-ea-dropdown-experiment-popup-container">
				<EditionAwarenessDropdownExperimentLoadable
					paymentFlowUrl={paymentFlowUrl}
					changePlanUrl={changePlanUrl}
					hasBillingInformation={hasBillingInformation}
					canAddBillingForTrial={canAddBillingForTrial || isSiteAdmin}
					billingSourceSystem={billingSourceSystem as BillingSourceSystem}
					daysLeftInTrial={daysLeftInTrial ?? 0}
				/>
			</div>
		);
	}

	return (
		<PremiumPopupBackground data-testid="premium-popup-background">
			<ImageContainer>
				<PremiumImage />
			</ImageContainer>
			<PopupBodyContainer>
				<PremiumHeaderTitle>
					<Heading size="small">
						<FormattedMessage {...i18n.premiumTrial} />
					</Heading>
				</PremiumHeaderTitle>
				<ActionMenuButtons
					hasBillingInformation={hasBillingInformation}
					isPreDunningPeriod={isPreDunningPeriod}
					trialEndDate={trialEndDate}
				/>
			</PopupBodyContainer>
		</PremiumPopupBackground>
	);
};

interface ActionMenuContentProps extends ActionMenuButtonsType {
	isPremiumOnboardingFeatureDiscoveryEnabled?: boolean;
	canAddBillingForTrial?: boolean;
	canAddBillingForTrialAnalyticsAttributes?: any;
	isPTORTEnabled?: boolean;
	billingSourceSystem?: string | null;
	isTreatmentForEaPremiumDropdownExperiment?: boolean;
	daysLeftInTrial?: number | null;
}

export const ActionMenuContent: React.FC<ActionMenuContentProps> = ({
	hasBillingInformation,
	isPreDunningPeriod,
	trialEndDate,
	isPremiumOnboardingFeatureDiscoveryEnabled,
	canAddBillingForTrial,
	canAddBillingForTrialAnalyticsAttributes,
	isPTORTEnabled,
	daysLeftInTrial,
	billingSourceSystem,
	isTreatmentForEaPremiumDropdownExperiment,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const pillType = getPillType({ isSiteAdmin, hasBillingInformation });

	useEffect(() => {
		const attributes = {
			...(isPremiumOnboardingFeatureDiscoveryEnabled && {
				pillType,
				isPTORTEnabled,
			}),
			...(canAddBillingForTrial && canAddBillingForTrialAnalyticsAttributes),
		};

		const analyticEvent = {
			type: 'sendScreenEvent',
			data: {
				name: 'premTrialActionScreen',
				attributes,
			},
		};
		createAnalyticsEvent(analyticEvent).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		createAnalyticsEvent,
		pillType,
		isPremiumOnboardingFeatureDiscoveryEnabled,
		canAddBillingForTrial,
		isPTORTEnabled,
		isTreatmentForEaPremiumDropdownExperiment,
		daysLeftInTrial,
		billingSourceSystem,
		// UseEffect can't properly handle objects as dependencies, so we need to use the object's properties
		canAddBillingForTrialAnalyticsAttributes?.isBillingAdmin,
		canAddBillingForTrialAnalyticsAttributes?.isSiteAdmin,
	]);

	return (
		<MenuGroup minWidth={320} onClick={(e) => e.stopPropagation()}>
			<PremiumActionMenuItems
				hasBillingInformation={hasBillingInformation}
				isPreDunningPeriod={isPreDunningPeriod}
				trialEndDate={trialEndDate}
				daysLeftInTrial={daysLeftInTrial}
				billingSourceSystem={billingSourceSystem}
				isTreatmentForEaPremiumDropdownExperiment={isTreatmentForEaPremiumDropdownExperiment}
				canAddBillingForTrial={canAddBillingForTrial}
			/>
		</MenuGroup>
	);
};
